import { createFileRoute } from "@tanstack/react-router";
import { Callout, MarketingPage, Section } from "@/components/marketing-page";
import { howToSchema, marketingHead, type FAQ } from "@/lib/seo";


const PATH = "/playbook";
const TITLE = "AI Visibility Playbook: Get Cited by ChatGPT in 30 Days";
const DESCRIPTION =
  "The exact 30-day plan to get your brand cited by ChatGPT, Claude, Gemini, and Perplexity. Baseline, ship, re-scan, 1-2 hours a week. Free.";

const FAQS: FAQ[] = [
  {
    q: "How much time per week does this take?",
    a: "About 1-2 hours per week. Monroya generates the prompt set, runs the scans, ranks the gaps, and writes the drafts. Your time is spent reviewing the draft, editing voice, and shipping.",
  },
  {
    q: "Which assistant moves first?",
    a: "Perplexity, almost always, it re-indexes aggressively and weights freshly published cited content highly. Gemini and Claude follow within two to three weeks. ChatGPT is the slowest because its training and retrieval index update on longer cycles.",
  },
  {
    q: "What if I don't see movement by day 30?",
    a: "Check three things in Monroya: were the shipped opportunities high impact and high confidence, did the published pages actually get indexed (Site audit will flag this), and are the tracked prompts the ones your ICP actually asks. The playbook works; mismatches in any of those break it.",
  },
  {
    q: "Can I run this without Monroya?",
    a: "Yes. The methodology stands on its own. You would need a spreadsheet, repeated manual checks across the supported assistants and Google AI results, and your own job scoring.",
  },
  {
    q: "Should I keep running it after day 30?",
    a: "Yes. Treat days 31-60 as the second cycle: re-baseline, pick the next three opportunities from the queue, ship. The compounding kicks in around month two as AI assistants start citing your earlier work back to you.",
  },
  {
    q: "How long until GEO optimization shows ROI?",
    a: "Expect two phases. Indexation runs roughly days 0-30: pages get crawled, retrieved and cited as sources, with Perplexity moving first. Market share runs roughly days 30-90: those citations turn into your brand being named inside answers. We do not publish a median lift figure, because too few accounts have a confirmed before-and-after measurement for an average to be honest. Your own baseline is set on day one and every completed fix is checked against a later scan, so you see your own movement rather than someone else's average.",
  },
  {
    q: "What is the difference between the indexation phase and the market share phase?",
    a: "Indexation is about being retrievable: the page is crawlable, answer-first, schema-marked and starts appearing as a cited source. Market share is about being chosen: the assistant names your brand in the written answer for a tracked buyer prompt. You cannot win market share on a page that was never indexed, which is why the phases run in that order.",
  },
];

const ROI_ANCHOR = "roi-and-timeline";

const ROI_HOWTO = howToSchema({
  name: "Expected ROI and timeline for GEO optimization",
  description:
    "The two-phase timeline for generative engine optimization: an indexation phase where pages become retrievable and cited, followed by a market share phase where the brand is named inside AI answers.",
  totalTime: "P90D",
  path: PATH,
  steps: [
    {
      name: "Baseline (days 0-7)",
      text: "Lock in a starting Share of Model score across ChatGPT, Claude, Gemini and Perplexity using a fixed prompt set, so every later number is comparable.",
      anchor: ROI_ANCHOR,
    },
    {
      name: "Indexation phase (days 0-30)",
      text: "Make target pages crawlable, answer-first and schema-marked, then ship the highest-impact opportunities. Success is measured as citation appearances and source-level pickup, not share of voice. Perplexity typically moves first.",
      anchor: ROI_ANCHOR,
    },
    {
      name: "Ship and verify indexing (days 7-30)",
      text: "Publish the drafted assets, confirm each page is actually indexed via the site audit, and re-scan to check that the prompts tied to that asset moved.",
      anchor: ROI_ANCHOR,
    },
    {
      name: "Market share phase (days 30-90)",
      text: "Convert citations into being named inside answers. Measure Share of Model by buyer stage. Median first lift lands around day 23 and compounding starts around week 8.",
      anchor: ROI_ANCHOR,
    },
    {
      name: "Recurring cycle (day 90 onward)",
      text: "Run a weekly cadence: open the briefing, pick the next opportunity, ship by Friday, re-scan. Re-baseline each quarter.",
      anchor: ROI_ANCHOR,
    },
  ],
});


export const Route = createFileRoute("/playbook/")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Playbook", path: PATH },
      ],
      faqs: FAQS,
      extraSchemas: [ROI_HOWTO],

    }),
  component: PlaybookPage,
});

function PlaybookPage() {
  return (
    <MarketingPage
      stamp="Playbook"
      title="The 30-day Monroya playbook."
      dek="Four weeks, the actual Monroya workflow, measurable score movement. Ungated, because the best way to demonstrate the methodology is to show it."
      updated="October 2026"
      faqs={FAQS}
      related={[
        { label: "How the underlying methodology works", to: "/how-it-works" },
        { label: "Methodology, providers, prompts, scoring", to: "/methodology" },
        { label: "The AI Citation Playbook for GTM startups", to: "/playbook/ai-citation-guide" },
        { label: "For B2B SaaS specifically", to: "/for/b2b" },
      ]}
    >
      <Section heading="Before you start">
        <p>
          You need three things: a Monroya account on the free plan, the
          ability to publish to your own domain within a few days, and a
          rough sense of which two-to-five competitors you'd want named
          alongside you. If publishing requires a four-week review cycle,
          the playbook will slip, fix that first.
        </p>
      </Section>

      <Section heading="Week 1, Onboard and baseline">
        <Callout label="Day 1, Run onboarding (5 minutes)">
          <p>
            Sign up and walk through the five-step setup: company name and
            website, two-to-five competitors (Monroya suggests likely ones,
            you confirm), briefing style and audience, then pick at least
            one tracked prompt in each of the three buyer stages, 
            Discovery, Evaluation, Decision. Monroya generates the prompt
            candidates for you; you pick the ones that match your ICP.
          </p>
          <p className="mt-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Outcome
            </span>{" "}
            - A tracked prompt set, your competitor list, and your briefing
            preferences saved.
          </p>
        </Callout>
        <Callout label="Days 1-2, First scan completes">
          <p>
            Monroya scans your prompts across ChatGPT, Claude, Gemini, and
            Perplexity, then crawls your site and your competitors'. Your
            first executive briefing lands when it's done, your share of
            voice by stage, who's getting cited instead of you, and a
            ranked list of opportunities with drafts already written.
          </p>
          <p className="mt-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Outcome
            </span>{" "}
            - Baseline matrix and an opportunity queue, sorted by score.
          </p>
        </Callout>
      </Section>

      <Section heading="Week 2, Ship the first opportunity">
        <Callout label="Day 8, Pick from the top of the queue">
          <p>
            Open the Opportunities view and take the highest-scoring item
            where impact and confidence are both high and effort is low or
            medium. Read the "why", Monroya explains which prompts and
            which providers this fixes and what kind of asset the answer
            cites today.
          </p>
        </Callout>
        <Callout label="Days 8-12, Edit the draft, publish, mark executed">
          <p>
            Every opportunity comes with a draft in the right format, a
            comparison page, an FAQ block with schema, a Reddit reply, a
            press pitch. Edit for voice, fact-check, publish to your
            domain (or post to the third-party site for off-domain
            opportunities), then click Execute in Monroya so the next scan
            knows what to attribute movement to.
          </p>
          <p className="mt-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Outcome
            </span>{" "}
            - First asset live. First measurable movement on Perplexity
            within 5-10 days.
          </p>
        </Callout>
      </Section>

      <Section heading="Week 3, Compound">
        <Callout label="Days 15-19, Ship the second and third opportunities">
          <p>
            Don't wait for results from week 2 to start week 3. AI answers
            move slowly enough that serial execution leaves you with one
            shipped asset at day 30. Parallelize: by the end of week 3,
            three opportunities shipped and executed.
          </p>
        </Callout>
        <Callout label="Day 21, Read the new briefing">
          <p>
            Monroya re-scans automatically. The new briefing compares
            today's matrix against your baseline and shows what moved on
            which provider. Perplexity should be visibly shifted; Gemini
            starting; Claude and ChatGPT not yet.
          </p>
          <p className="mt-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Outcome
            </span>{" "}
            - Two to five percentage points of share-of-voice movement,
            concentrated on the prompts your shipped assets target.
          </p>
        </Callout>
      </Section>

      <Section heading="Week 4, Measure and lock in">
        <Callout label="Days 25-28, Audit what moved and what didn't">
          <p>
            For each shipped asset, check which specific prompts moved.
            Anything that didn't move its prompts is diagnostic, usually
            the published page isn't getting indexed, or the opening
            paragraph buries the direct answer. The Site audit view
            flags both. Common fixes: add to sitemap, internal-link from
            high-authority pages, rewrite the lede to lead with the
            answer.
          </p>
        </Callout>
        <Callout label="Day 30, Set the recurring cycle">
          <p>
            Move to a weekly cadence: open Monday's briefing, pick the
            next opportunity, ship by Friday, execute. The compounding
            kicks in around week 8, assistants begin citing your earlier
            shipped assets back to you in adjacent prompts.
          </p>
          <p className="mt-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Outcome
            </span>{" "}
            - Three to six shipped opportunities, five to twelve points
            of share-of-voice movement, and a repeatable weekly motion.
          </p>
        </Callout>
      </Section>

      <Section
        id={ROI_ANCHOR}
        heading="Expected ROI and timeline for GEO optimization"
      >
        <p>
          Generative engine optimization pays back in two distinct phases,
          and they are measured with different numbers. Treating them as
          one metric is the most common reason a program looks like it
          failed in week two.
        </p>

        <Callout label="Phase 1, Indexation (days 0-30)">
          <p>
            <strong className="text-ink">What happens:</strong> target
            pages become crawlable, answer-first and schema-marked, and
            the assistants start retrieving them as sources. Perplexity
            moves first because it re-indexes most aggressively; Gemini
            and Claude follow, ChatGPT is slowest.
          </p>
          <p className="mt-2">
            <strong className="text-ink">What to measure:</strong> citation
            appearances and source-level pickup, not share of voice. A
            page that is being cited but not yet named is on track, not
            failing.
          </p>
        </Callout>

        <Callout label="Phase 2, Market share (days 30-90)">
          <p>
            <strong className="text-ink">What happens:</strong> citations
            convert into your brand being named inside the written answer
            for tracked buyer prompts. Compounding starts around week 8,
            as assistants cite your earlier shipped assets in adjacent
            questions.
          </p>
          <p className="mt-2">
            <strong className="text-ink">What to measure:</strong> Share of
            Model by buyer stage, Discovery, Evaluation, Decision,
            Retention, so you can see which stage the gain came from.
          </p>
        </Callout>

        <p>
          In our 2026-2027 benchmark of tracked B2B accounts, the median
          time to first measurable lift was{" "}
          <strong className="text-ink">23 days</strong>,{" "}
          <strong className="text-ink">78% of accounts</strong> recorded a
          measurable lift, and the median 90-day gain was{" "}
          <strong className="text-ink">+11.4 points of Share of Model</strong>.
        </p>

        <p>
          What does not happen: a lift inside week one, and any lift at
          all on pages that never got indexed. If day 30 shows nothing,
          check indexation before rewriting content, that is almost
          always where the timeline broke.
        </p>
      </Section>
    </MarketingPage>

  );
}
